import React from 'react';
import Text from 'antd/lib/typography/Text';
import Dropdown from 'antd/lib/dropdown/dropdown';
import { useSidebarColors } from '../../styles/themeMode';
import { getNavDropdownMenuStyle } from '../../styles/globalStyles';
import { CaretDownOutlined } from '@ant-design/icons';
import { MIN_ORG_METRIC_PERIOD_IN_DAYS, OrgOverviewDatePickerTimeRange } from './OrgOverviewDatePickerTimeRange';
import dayjs, { Dayjs } from 'dayjs';

export enum OrgNavigationDateRangeType {
    Custom = 'Custom date range',
    Yesterday = 'Yesterday',
    ThisWeek = 'This week',
    LastWeek = 'Last week',
    LastTwoWeeks = 'Last two weeks',
    ThisMonth = 'This month',
    LastMonth = 'Last month',
    LastQuarter = 'Last quarter',
    LastYear = 'Last year',
    ThisYear = 'This year',
}

export interface OrgMetricsDateRange {
    start: string;
    end: string;
}

export function getDateRangeForType(selectedDateRangeType: OrgNavigationDateRangeType): OrgMetricsDateRange | null {
    let startDate: Dayjs;
    let endDate: Dayjs;
    const today = dayjs();

    switch (selectedDateRangeType) {
        case OrgNavigationDateRangeType.Yesterday:
            startDate = today.subtract(1, 'day').startOf('day');
            endDate = today.subtract(1, 'day').endOf('day');
            break;
        case OrgNavigationDateRangeType.ThisWeek:
            startDate = today.startOf('week');
            endDate = today.endOf('day');
            break;
        case OrgNavigationDateRangeType.LastWeek:
            startDate = today.subtract(1, 'week').startOf('week');
            endDate = today.subtract(1, 'week').endOf('week');
            break;
        case OrgNavigationDateRangeType.LastTwoWeeks:
            startDate = today.subtract(13, 'day').startOf('day');
            endDate = today.endOf('day');
            break;
        case OrgNavigationDateRangeType.ThisMonth:
            startDate = today.startOf('month');
            endDate = today.endOf('day');
            break;
        case OrgNavigationDateRangeType.LastMonth:
            startDate = today.subtract(1, 'month').startOf('month');
            endDate = today.subtract(1, 'month').endOf('month');
            break;
        case OrgNavigationDateRangeType.LastQuarter: {
            const currentQuarterStartMonth = Math.floor(today.month() / 3) * 3;
            const lastQuarter = today.month(currentQuarterStartMonth).subtract(3, 'month');

            startDate = lastQuarter.startOf('month');
            endDate = lastQuarter.add(2, 'month').endOf('month');
            break;
        }
        case OrgNavigationDateRangeType.ThisYear:
            startDate = today.startOf('year');
            endDate = today.endOf('day');
            break;
        case OrgNavigationDateRangeType.LastYear:
            startDate = today.subtract(1, 'year').startOf('year');
            endDate = today.subtract(1, 'year').endOf('year');
            break;
        case OrgNavigationDateRangeType.Custom:
            return {
                start: today.subtract(MIN_ORG_METRIC_PERIOD_IN_DAYS, 'days').format('YYYY-MM-DD'),
                end: today.format('YYYY-MM-DD'),
            };
        default:
            return null;
    }

    return {
        start: startDate.format('YYYY-MM-DD'),
        end: endDate.format('YYYY-MM-DD'),
    };
}

interface OrgNavigationDateRangeProps {
    selectedDateRange: OrgMetricsDateRange;
    setSelectedDateRange: (value: OrgMetricsDateRange) => void;
    selectedDateRangeType: OrgNavigationDateRangeType;
    setSelectedDateRangeType: (value: OrgNavigationDateRangeType) => void;
    setDateRange: (selectedDateRangeType: OrgNavigationDateRangeType, selectedDateRange: OrgMetricsDateRange) => void;
    isLoading?: boolean;
}

export const OrgNavigationDateRange: React.FC<OrgNavigationDateRangeProps> = ({
    selectedDateRange,
    setSelectedDateRange,
    selectedDateRangeType,
    setSelectedDateRangeType,
    setDateRange,
    isLoading,
}) => {
    const sidebarColors = useSidebarColors();
    const isCustomRange = selectedDateRangeType === OrgNavigationDateRangeType.Custom;

    const getOrgNavigationDateRangeMenuItems = () => {
        return Object.entries(OrgNavigationDateRangeType).map(([key, label]) => ({
            key,
            label: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0px',
                        borderRadius: 'inherit',
                    }}
                >
                    <Text style={{ color: sidebarColors.text, fontSize: 14 }}>{label}</Text>
                </div>
            ),
        }));
    };

    return (
        <div style={{ paddingBottom: 10 }}>
            <Dropdown
                menu={{
                    items: getOrgNavigationDateRangeMenuItems(),
                    onClick: (item) => {
                        const nextSelectedDateRangeType = OrgNavigationDateRangeType[item.key];

                        const nextSelectedDateRange = getDateRangeForType(nextSelectedDateRangeType);
                        if (nextSelectedDateRange) {
                            setDateRange(nextSelectedDateRangeType, nextSelectedDateRange);
                            return;
                        }

                        setSelectedDateRangeType(nextSelectedDateRangeType);
                    },
                    style: getNavDropdownMenuStyle(sidebarColors),
                }}
                trigger={['click']}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '5px 12px',
                        border: `1px solid ${sidebarColors.border}`,
                        borderWidth: isCustomRange ? `1px 1px 0 1px` : 1,
                        borderRadius: isCustomRange ? `4px 4px 0 0` : 4,
                        backgroundColor: sidebarColors.dropdownBg,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text style={{ color: sidebarColors.text, fontSize: 14 }}>{selectedDateRangeType}</Text>
                    </div>
                    <CaretDownOutlined style={{ color: sidebarColors.textMuted, fontSize: 16, marginLeft: 12 }} />
                </div>
            </Dropdown>
            {isCustomRange && (
                <OrgOverviewDatePickerTimeRange
                    currentDateRange={selectedDateRange}
                    onFinish={({ metricsDateRange }) => {
                        if (metricsDateRange?.length < 2) {
                            return;
                        }

                        setSelectedDateRange({
                            start: metricsDateRange[0].format('YYYY-MM-DD'),
                            end: metricsDateRange[1].format('YYYY-MM-DD'),
                        });
                    }}
                    isLoading={isLoading}
                />
            )}
        </div>
    );
};
